
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import { formatDate } from '@/utils/index'
import { DeptType, IAllDeptInfo, IAllProjectInfo, IDept } from '@/api/types'
import {
  ListProjects,
  CreateProject,
  UpdateProject,
  EnableProject,
  DisableProject,
  ListDepts
} from '@/api/orgAdmin'

@Component({
  name: 'ProjectManagement'
})
export default class extends Vue {
  public projectList: IAllProjectInfo[] = []
  public deptList: IDept[] = []
  public listLoading = true
  public deptLoading = false

  public projectQuery = {
    onlyEnabled: undefined as boolean | undefined
  }

  public statusOptions = [
    { label: '全部', value: undefined },
    { label: '启用', value: true },
    { label: '关闭', value: false }
  ]

  public get detectDeptList(): IDept[] {
    return this.deptList.filter(dept => dept.type == DeptType.Detect)
  }

  // 创建项目相关
  public createDialogVisible = false
  public createForm = {
    name: '',
    prefix: '',
    deptId: 0
  }

  public createRules = {
    name: [
      { required: true, message: '请输入项目名称', trigger: 'blur' },
      { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    prefix: [
      { required: true, message: '请输入项目前缀', trigger: 'blur' },
      { min: 2, max: 5, message: '长度在 2 到 5 个字符', trigger: 'blur' },
    ],
    deptId: [
      { required: true, message: '请选择所属部门', trigger: 'change' }
    ]
  }

  // 编辑项目相关
  public updateDialogVisible = false
  public updateForm = {
    id: 0,
    name: '',
    prefix: '',
    deptId: 0
  }

  public updateRules = {
    name: [
      { required: true, message: '请输入项目名称', trigger: 'blur' },
      { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    prefix: [
      { required: true, message: '请输入项目前缀', trigger: 'blur' },
      { min: 2, max: 5, message: '长度在 2 到 5 个字符', trigger: 'blur' },
      { pattern: /^[A-Z0-9]+$/, message: '只能包含大写字母和数字', trigger: 'blur' }
    ],
    deptId: [
      { required: true, message: '请选择所属部门', trigger: 'change' }
    ]
  }

  created() {
    this.getProjectList()
    this.getDeptList()
  }

  public async getDeptList() {
    this.deptLoading = true
    try {
      const { data } = await ListDepts()
      this.deptList = this.flattenDepts(data)
      this.deptLoading = false
    } catch (error) {
      console.error('获取部门列表失败:', error)
      this.$message.error('获取部门列表失败')
      this.deptLoading = false
    }
  }

  private flattenDepts(depts: IAllDeptInfo[]): IDept[] {
    const result: IDept[] = []
    if (!depts || depts.length === 0) {
      return result
    }
    const flatten = (items: IAllDeptInfo[]) => {
      items.forEach(item => {
        result.push(item.dept)
        if (item.children && item.children.length > 0) {
          flatten(item.children)
        }
      })
    }
    flatten(depts)
    return result
  }

  public async getProjectList() {
    this.listLoading = true
    try {
      const { data } = await ListProjects(this.projectQuery)
      this.projectList = data
      this.listLoading = false
    } catch (error) {
      console.error('获取项目列表失败:', error)
      this.$message.error('获取项目列表失败')
      this.listLoading = false
    }
  }

  public handleFilter() {
    this.getProjectList()
  }

  public formatTime(time: string) {
    if (!time) return ''
    return formatDate(time, 'YYYY-MM-DD HH:mm:ss')
  }

  // 创建项目相关方法
  public handleCreate() {
    this.resetCreateForm()
    this.createDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.createForm as ElForm).clearValidate()
    })
  }

  public resetCreateForm() {
    this.createForm = {
      name: '',
      prefix: '',
      deptId: this.deptList.length > 0 ? this.deptList[0].id : 0
    }
  }

  public createData() {
    (this.$refs.createForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await CreateProject(this.createForm)
          this.createDialogVisible = false
          this.$message.success('创建项目成功')
          this.getProjectList()
        } catch (error) {
          console.error('创建项目失败:', error)
          this.$message.error('创建项目失败')
        }
      }
    })
  }

  // 编辑项目相关方法
  public handleUpdate(row: IAllProjectInfo) {
    this.updateForm = {
      id: row.project.id,
      name: row.project.name,
      prefix: row.project.prefix,
      deptId: row.project.deptId || (this.deptList.length > 0 ? this.deptList[0].id : 0)
    }
    this.updateDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.updateForm as ElForm).clearValidate()
    })
  }

  public async updateData() {
    (this.$refs.updateForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await UpdateProject(this.updateForm)
          this.updateDialogVisible = false
          this.$message.success('更新项目成功')
          this.getProjectList()
        } catch (error) {
          console.error('更新项目失败:', error)
          this.$message.error('更新项目失败')
        }
      }
    })
  }

  // 启用/关闭项目相关方法
  public async handleEnable(row: IAllProjectInfo) {
    try {
      await this.$confirm('确定要启用该项目吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })

      await EnableProject({ id: row.project.id })
      this.$message.success('启用项目成功')
      this.getProjectList()
    } catch (error) {
      if (error !== 'cancel') {
        console.error('启用项目失败:', error)
        this.$message.error('启用项目失败')
      }
    }
  }

  public async handleDisable(row: IAllProjectInfo) {
    try {
      await this.$confirm('确定要关闭该项目吗?', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })

      await DisableProject({ id: row.project.id })
      this.$message.success('关闭项目成功')
      this.getProjectList()
    } catch (error) {
      if (error !== 'cancel') {
        console.error('关闭项目失败:', error)
        this.$message.error('关闭项目失败')
      }
    }
  }
}
